import React, { useMemo } from 'react'
import { Tree } from '../types'
import { getTreeDisplayName, getTreeIssues } from '../utils/treeUtils'
import { getPatchedTree } from '../store/patchStore'
import { useOrchards, useTreeStore } from '../store/useTreeStore'
import styles from '../styles/tree-list.module.css'

interface TreeListProps {
  selectedTreeId: number | null
  onTreeSelect: (tree: Tree) => void
  onClose?: () => void
}

const TreeList: React.FC<TreeListProps> = ({ onTreeSelect, selectedTreeId, onClose }) => {
  const orchards = useOrchards()
  const { trees, isLoading, error } = useTreeStore()

  // Sort trees by ID for consistent ordering
  const sortedTrees = useMemo(() => {
    return [...trees].sort((a, b) => a.id - b.id)
  }, [trees])

  // Prevent wheel events from bubbling up to the map when scrolling is possible
  const handleWheel = (e: React.WheelEvent) => {
    const target = e.currentTarget as HTMLElement
    const { scrollTop, scrollHeight, clientHeight } = target
    const isScrollable = scrollHeight > clientHeight
    
    if (isScrollable) {
      // Check if we're at the boundaries with a small tolerance
      const tolerance = 1
      const isAtTop = scrollTop <= tolerance
      const isAtBottom = scrollTop >= scrollHeight - clientHeight - tolerance
      
      // Determine if we can scroll in the direction of the wheel event
      const scrollingUp = e.deltaY < 0
      const scrollingDown = e.deltaY > 0
      
      // If we can scroll in the direction of the wheel event, stop propagation
      // This allows scrolling to work in Safari
      const canScroll = (scrollingUp && !isAtTop) || (scrollingDown && !isAtBottom)
      
      if (canScroll) {
        // We can scroll, so prevent the event from reaching the map
        e.stopPropagation()
      }
      // If we can't scroll (at boundaries), let the event bubble to the map
      // This allows map zoom when the list is at the top/bottom
    }
  }

  // Handle touch events for iPad scrolling
  const handleTouchStart = (e: React.TouchEvent) => {
    // Allow touch events to proceed for scrolling
    // Stop propagation to prevent map interaction
    e.stopPropagation()
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    // Allow touch move events to proceed for scrolling
    // Stop propagation to prevent map interaction
    e.stopPropagation()
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    // Allow touch end events to proceed
    // Stop propagation to prevent map interaction
    e.stopPropagation()
  }

  if (isLoading) {
    return (
      <div className={styles['tree-list']}>
        <div className={styles['tree-list-header']}>
          <h3>Bäume</h3>
          {onClose && (
            <button 
              className={styles['close-button']} 
              onClick={onClose}
              title="Bäume-Liste schließen"
            >
              ×
            </button>
          )}
        </div>
        <div 
          className={styles['tree-list-content']} 
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <p>Bäume werden geladen...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles['tree-list']}>
        <div className={styles['tree-list-header']}>
          <h3>Bäume</h3>
          {onClose && (
            <button 
              className={styles['close-button']} 
              onClick={onClose}
              title="Bäume-Liste schließen"
            >
              ×
            </button>
          )}
        </div>
        <div 
          className={styles['tree-list-content']} 
          onWheel={handleWheel}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <p className={styles.error}>Fehler: {error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles['tree-list']}>
      <div className={styles['tree-list-header']}>
        <h3>Bäume ({trees.length})</h3>
        {onClose && (
          <button 
            className={styles['close-button']} 
            onClick={onClose}
            title="Bäume-Liste schließen"
          >
            ×
          </button>
        )}
      </div>

      <div 
        className={styles['tree-list-content']} 
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {trees.length === 0 ? (
          <p>Keine Bäume in diesem Bereich gefunden.</p>
        ) : (
          <ul className={styles['tree-items']}>
            {sortedTrees.map((tree) => {
              const patchedTree = getPatchedTree(tree)
              const { errors, warnings, todos } = getTreeIssues(patchedTree, orchards)
              const hasErrors = errors.length > 0
              const hasWarnings = warnings.length > 0
              const hasTodos = todos.length > 0
              const isSelected = tree.id === selectedTreeId

              let itemClassName = styles['tree-item']
              if (isSelected) {
                itemClassName += ` ${styles['tree-item-selected']}`
              } else if (hasErrors) {
                itemClassName += ` ${styles['tree-item-error']}`
              } else if (hasWarnings) {
                itemClassName += ` ${styles['tree-item-warning']}`
              } else if (hasTodos) {
                itemClassName += ` ${styles['tree-item-todo']}`
              }

              const hasPatch = false // No patch logic here as per new_code

              return (
                <li key={tree.id} className={itemClassName} onClick={() => onTreeSelect(tree)}>
                  <div className={styles['tree-name']}>
                    {getTreeDisplayName(tree)}
                    {hasPatch && (
                      <span className={styles['tree-updated-label']}>aktualisiert</span>
                    )}
                    {isSelected && (
                      <span className={styles['selected-indicator']}>✓</span>
                    )}
                  </div>
                  <div className={styles['tree-details']}>
                    <span className={styles['tree-id']}>OSM ID: {tree.id}</span>
                    {tree.properties.species && (
                      <span className={styles['tree-species']}>
                        {tree.properties.species}
                      </span>
                    )}
                    {(hasErrors || hasWarnings || hasTodos) && (
                      <div className={styles['tree-issues-indicator']}>
                        {hasErrors && <span className={styles['issue-badge']} title={`${errors.length} Fehler`}>❌ {errors.length}</span>}
                        {hasWarnings && <span className={styles['issue-badge']} title={`${warnings.length} Warnungen`}>⚠️ {warnings.length}</span>}
                        {hasTodos && <span className={styles['issue-badge']} title={`${todos.length} Vorschläge`}>💡 {todos.length}</span>}
                      </div>
                    )}
                  </div>
                </li>
              )
          })}
        </ul>
      )}
    </div>
  </div>
  )
}

export default TreeList 